/**
 * textpadCaret — чистые функции каретки TextPad (06.09, владелец: «курсор
 * должен перейти туда, где произошёл клик»). Все правки — относительно
 * каретки, а не «в конец»: вставка в caret, удаление символа ПЕРЕД кареткой,
 * замена символа перед кареткой (ё по лонгпрессу). Плюс две DOM-мелочи для
 * превью шита: индекс каретки по точке тапа и докрутка превью к каретке.
 */
export interface EditResult { v: string; caret: number }
/** Правка значения относительно каретки; null = ничего не менять.
 *  selEnd (18.09, Даша: «выделяешь текст, стираешь — кнопка не работает») — конец
 *  выделения в поле хоста: selEnd > caret → правка работает с [caret, selEnd) целиком. */
export type Edit = (v: string, caret: number, selEnd?: number) => EditResult | null;

export const clampCaret = (c: number, len: number) => Math.max(0, Math.min(Number.isFinite(c) ? Math.trunc(c) : len, len));

/* 27.09: эмодзи и прочие символы вне BMP — две UTF-16 половинки; каретка между ними ломала бы
   символ при следующей правке. true — позиция i стоит внутри такой пары. */
const inPair = (v: string, i: number) => i > 0 && i < v.length
  && (v.charCodeAt(i - 1) & 0xfc00) === 0xd800 && (v.charCodeAt(i) & 0xfc00) === 0xdc00;

/** Вставка текста в позицию каретки с учётом лимита (0 = без лимита); хвост после каретки
 *  сохраняется. Есть выделение — оно заменяется текстом (освобождённое место идёт в лимит). */
export function insertAt(v: string, caret: number, text: string, max = 0, selEnd = caret): EditResult | null {
  const c = clampCaret(caret, v.length), e = Math.max(c, clampCaret(selEnd, v.length));
  const room = max ? max - (v.length - (e - c)) : Infinity;
  const t = room > 0 ? text.slice(0, room) : "";
  if (!t) return null;
  return { v: v.slice(0, c) + t + v.slice(e), caret: c + t.length };
}

/** ⌫ — выделение целиком, иначе символ ПЕРЕД кареткой; в начале строки ничего не делает. */
export function deleteBefore(v: string, caret: number, selEnd = caret): EditResult | null {
  const c = clampCaret(caret, v.length), e = Math.max(c, clampCaret(selEnd, v.length));
  if (e > c) return { v: v.slice(0, c) + v.slice(e), caret: c };
  if (c === 0) return null;
  const from = inPair(v, c - 1) ? c - 2 : c - 1;   /* эмодзи стирается целиком, а не половинкой */
  return { v: v.slice(0, from) + v.slice(c), caret: from };
}

/** ◀ ▶ (27.09, владелец: «добавить возможность перемещать курсор, чтобы стереть только одну
 *  букву — сейчас можно только слово целиком»): каретка на символ влево/вправо. Есть выделение —
 *  схлопнуть к его краю, как у ОС; у края строки — ничего (null). */
export function moveCaret(v: string, caret: number, d: -1 | 1, selEnd = caret): EditResult | null {
  const c = clampCaret(caret, v.length), e = Math.max(c, clampCaret(selEnd, v.length));
  if (e > c) return { v, caret: d < 0 ? c : e };
  let n = clampCaret(c + d, v.length);
  if (inPair(v, n)) n += d;                          /* через эмодзи — одним шагом */
  return n === c ? null : { v, caret: n };
}

/** Лонгпресс «е»→«ё»: символ перед кареткой равен base (в любом регистре) → alt в том же регистре. */
export function replaceBefore(v: string, caret: number, base: string, alt: string): EditResult | null {
  const c = clampCaret(caret, v.length);
  if (c === 0) return null;
  const last = v.charAt(c - 1);
  if (last.toLowerCase() !== base.toLowerCase()) return null;
  const isUp = last !== last.toLowerCase();
  return { v: v.slice(0, c - 1) + (isUp ? alt.toUpperCase() : alt.toLowerCase()) + v.slice(c), caret: c };
}

/** Замена диапазона [start,end) на text с лимитом; каретка сдвигается вслед за хвостом либо встаёт после вставки. */
export function replaceRange(v: string, caret: number, start: number, end: number, text: string, max = 0): EditResult {
  const s = clampCaret(start, v.length), e = Math.max(s, clampCaret(end, v.length));
  const room = max ? max - (v.length - (e - s)) : Infinity;
  const t = room > 0 ? text.slice(0, room) : "";
  const next = v.slice(0, s) + t + v.slice(e);
  const c = caret >= e ? caret - (e - s) + t.length : caret > s ? s + t.length : caret;
  return { v: next, caret: clampCaret(c, next.length) };
}

/* ---------- DOM: превью шита ---------- */

/* caretPositionFromPoint есть не везде (Safari до 2025 — только caretRangeFromPoint). */
type CaretDoc = Document & Partial<Pick<Document, "caretPositionFromPoint" | "caretRangeFromPoint">>;

/** Длина текста во всех текстовых узлах контейнера до узла `stop` (не включая его). */
function textBefore(root: Node, stop: Node | null): number {
  let n = 0;
  const walk = (node: Node): boolean => {
    if (node === stop) return true;
    if (node.nodeType === Node.TEXT_NODE) { n += node.textContent?.length ?? 0; return false; }
    for (const ch of Array.from(node.childNodes)) if (walk(ch)) return true;
    return false;
  };
  walk(root);
  return n;
}

/**
 * Индекс каретки в полном тексте по точке тапа (превью разбито на два текстовых
 * узла вокруг <span class=caret>). Тап правее конца строки — конец строки;
 * мимо текста (паддинг/ниже) — length. Плейсхолдер (val === "") → 0.
 */
export function caretFromPoint(root: HTMLElement, x: number, y: number, len: number): number {
  if (!len) return 0;
  const doc = document as CaretDoc;
  let node: Node | null = null, offset = 0;
  if (typeof doc.caretPositionFromPoint === "function") {
    const p = doc.caretPositionFromPoint(x, y);
    if (p) { node = p.offsetNode; offset = p.offset; }
  } else if (typeof doc.caretRangeFromPoint === "function") {
    const r = doc.caretRangeFromPoint(x, y);
    if (r) { node = r.startContainer; offset = r.startOffset; }
  }
  if (!node || !root.contains(node)) return len;
  if (node.nodeType === Node.TEXT_NODE) return clampCaret(textBefore(root, node) + offset, len);
  /* элемент: offset — индекс дочернего узла → текст ДО него; за последним (или сам
     <span caret> без детей) — текст до элемента плюс его собственный */
  const child = node.childNodes[offset] ?? null;
  if (child) return clampCaret(textBefore(root, child), len);
  return clampCaret(textBefore(root, node) + (node.textContent?.length ?? 0), len);
}

/** Докрутить контейнер превью так, чтобы каретка была видна (только по вертикали). */
export function scrollToCaret(container: HTMLElement, caretEl: HTMLElement | null): void {
  if (!caretEl) { container.scrollTop = container.scrollHeight; return; }
  const pr = container.getBoundingClientRect(), cr = caretEl.getBoundingClientRect();
  if (cr.top < pr.top) container.scrollTop -= pr.top - cr.top + 4;
  else if (cr.bottom > pr.bottom) container.scrollTop += cr.bottom - pr.bottom + 4;
}
